import Router, { useRouter } from 'next/router';
import nProgress from 'nprogress';
import { ReactElement, ReactNode, useEffect, useState } from 'react';

// import {
//   Header,
//   OfflineShape,
//   Seo,
//   SideMenu,
// } from '@/shared/components/molecules';
const SideMenu = dynamic(
  () => import('@/shared/components/molecules').then((mod) => mod.SideMenu),
  {
    ssr: false,
  }
);
const Header = dynamic(
  () => import('@/shared/components/molecules').then((mod) => mod.Header),
  {
    ssr: false,
  }
);
const OfflineShape = dynamic(
  () => import('@/shared/components/molecules').then((mod) => mod.OfflineShape),
  { ssr: false }
);
const Seo = dynamic(
  () => import('@/shared/components/molecules').then((mod) => mod.Seo),
  { ssr: false }
);

import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import { isMobile } from 'react-device-detect';
import { useSessionStorage } from 'utils/general/storage';

import { cairo_font, roboto_font } from '@/_app-confiq';
import SwitchToDesktop from '@/shared/components/atoms/SwitchToDesktop';
import { ISeoProps } from '@/shared/components/molecules/Seo/ISeo';
import routes from '@/shared/constant/routes';
import { useAuth } from '@/shared/hooks/context/auth';
import {
  clearStoreData,
  useStore,
} from '@/shared/hooks/context/createGenericContext';
import { useFreeTrial } from '@/shared/hooks/useFreeTrial';
import { clsxm } from '@/shared/lib';

interface ILayoutProps extends ISeoProps {
  children: ReactElement;
  withSideMenu?: boolean;
  bgColor?: 'white' | 'grey' | 'light-grey';
  withContainer?: boolean;
  withHeader?: boolean;
  subHeader?: string | React.ReactElement;
  withBreadcrumbs?: ReactNode;
}
// const OPEN_SIDE_MENU_WIDTH = 216;
// const CLOSED_SIDE_MENU_WIDTH = 92;

export default function Layout({
  children,
  seoData,
  withSideMenu = true,
  withHeader = true,
  withContainer = true,
  bgColor = 'light-grey',
  subHeader,
  withBreadcrumbs,
}: ILayoutProps): ReactElement {
  const {
    i18n: { language },
  } = useTranslation();

  const { pathname } = useRouter();
  // assign page loading for global context
  const [storeData, setStore] = useStore((store) => store);
  const isDemoFarm = storeData?.isDemoFarm;
  const { collapseState, updateCollapseState, user } = useAuth();
  const { exitDemo } = useFreeTrial();

  const layoutClasses = clsxm(
    'min-h-screen',
    bgColor === 'white' && 'bg-white',
    bgColor === 'grey' && 'bg-background-body',
    bgColor === 'light-grey' && 'bg-background-neutral-100',
    withContainer ? 'container' : ''
  );
  const childrenLayoutClasses = clsxm(
    withSideMenu && [
      'mt-[62px] tablet:mt-0',
      collapseState
        ? `tablet:rtl:mr-216 tablet:ltr:ml-216`
        : `tablet:rtl:mr-92 tablet:ltr:ml-92`,
    ]
  );
  useEffect(() => {
    // const tempCollapseState = window?.sessionStorage?.getItem(
    //   'sideMenuCollapeState'
    // );
    // set default open/close sidemenu state
    // if (tempCollapseState) {
    //   updateCollapseState(JSON.parse(tempCollapseState));
    // } else {
    //   updateCollapseState(true);
    //   window.sessionStorage.setItem('sideMenuCollapeState', 'true');
    // }
    // make progress when page is loading
    const handleRouteStart = () => {
      setStore({ isLoadingPage: true });
      nProgress.start();
    };
    const handleRouteDone = (url: string) => {
      // TODO: IMP - check this
      if (routes.authenticate.includes(url)) clearStoreData();
      else setStore({ isLoadingPage: false });
      nProgress.done();
    };

    Router.events.on('routeChangeStart', handleRouteStart);
    Router.events.on('routeChangeComplete', handleRouteDone);
    Router.events.on('routeChangeError', handleRouteDone);

    return () => {
      // Make sure to remove the event handler on unmount!
      Router.events.off('routeChangeStart', handleRouteStart);
      Router.events.off('routeChangeComplete', handleRouteDone);
      Router.events.off('routeChangeError', handleRouteDone);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // set collapse state and save result in session storage
  const collapseHandler = () => {
    // window.sessionStorage.setItem(
    //   'sideMenuCollapeState',
    //   JSON.stringify(!collapseState)
    // );
    updateCollapseState(!collapseState);
  };

  const { getSessionStorageWithExpiry, setSessionStorageWithExpiry } =
    useSessionStorage();
  const switchToDesktop: boolean | undefined | null =
    getSessionStorageWithExpiry('switchToDesktop');
  const [shouldSwitchToDesktop, setShouldSwitchToDesktop] = useState<
    boolean | undefined
  >(undefined);

  useEffect(() => {
    if (isMobile) updateCollapseState(false);

    if (switchToDesktop !== null) setShouldSwitchToDesktop(switchToDesktop);
    else if (
      isMobile &&
      !pathname.includes(routes.authenticate) &&
      user?.companyUser === 1
    ) {
      setSessionStorageWithExpiry('switchToDesktop', true);
      setShouldSwitchToDesktop(true);
    } else setShouldSwitchToDesktop(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, user, switchToDesktop]);

  const handleCloseSwitchModal = () => {
    setSessionStorageWithExpiry('switchToDesktop', false);
    setShouldSwitchToDesktop(false);
    if (isDemoFarm) exitDemo();
  };

  if (shouldSwitchToDesktop === null || shouldSwitchToDesktop === undefined)
    return <></>;

  return (
    <div>
      <Seo seoData={seoData} />
      <style jsx global>{`
        html {
          font-family: ${language === 'ar'
            ? cairo_font.style.fontFamily
            : roboto_font.style.fontFamily};
        }
      `}</style>
      <section>
        {withSideMenu && (
          <SideMenu
            collapseHandler={collapseHandler}
            openCollapse={Boolean(collapseState)}
            // width={collapseState ? `w-230 ` : `w-92`}
          />
        )}
        {shouldSwitchToDesktop ? (
          <SwitchToDesktop handleCloseSwitchModal={handleCloseSwitchModal} />
        ) : (
          <div className={layoutClasses}>
            {/* offline */}
            <OfflineShape />
            <div className={childrenLayoutClasses}>
              {/* header */}
              {withHeader && (
                <Header
                  bgColor={bgColor}
                  subHeader={subHeader}
                  withBreadcrumbs={withBreadcrumbs}
                />
              )}
              {/* children */}
              {children}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
